import useFetchEntity from 'hooks/useFetchEntity';
import { useMemo, useRef } from 'react';

import "@fontsource/audiowide";
import "@fontsource/bebas-neue";
import "@fontsource/covered-by-your-grace";
import "@fontsource/economica";
import "@fontsource/righteous";
import "@fontsource/russo-one";

import DebugPrinter from 'components/Debug/DebugPrinter';
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import useFetchEntities from 'hooks/useFetchEntities';
import usePhaseIdTranslator from 'hooks/usePhaseIdTranslator';
import usePersistentStore from 'store/persistentStore';
import BackgroundSelector from './Filters/BackgroundSelector';
import IgImgPlacement from './Filters/IgImgPlacement';
import IgImgSelector from './Filters/IgImgSelector';
import MarginEditor from './Filters/MarginEditor';
import PhaseSelector from './Filters/PhaseSelector';
import useZoomByMouseWheel from './Hooks/useZoomByMouseWheel';
import { createForegroundPositionExpression, createHandleFgImgMouseMove, dateFormatIG } from './IGconfig';
import IGHeader from './Parts/IGHeader';
import TableOffenceDefence from './Parts/TableOffenceDefence';

const IG7_TeamOffenseVsDefense = ({ showData, exportRef }) => {
  const membership = usePersistentStore((state) => state.get('ig-membership'));
  const setMembership = usePersistentStore((state) => state.set('ig-membership'));
  const fgPictureUrl = usePersistentStore((state) => state.get('ig-tovd-fg-url'));
  const setFgPictureUrl = usePersistentStore((state) => state.set('ig-tovd-fg-url'));
  const size = usePersistentStore((state) => state.get('ig-tovd-size'));
  const setSize = usePersistentStore((state) => state.set('ig-tovd-size'));
  const SIZE_PRESETS = ['PORTRAIT', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 715, height: 1111, preset: 'PORTRAIT' };

  const fgImgPositionX = usePersistentStore((state) => state.get('ig-tovd-fgimgposition-x')) ?? 0;
  const setFgImgPositionX = usePersistentStore((state) => state.set('ig-tovd-fgimgposition-x'));
  const fgImgPositionY = usePersistentStore((state) => state.get('ig-tovd-fgimgposition-y')) ?? 0;
  const setFgImgPositionY = usePersistentStore((state) => state.set('ig-tovd-fgimgposition-y'));
  const fgImgZoom = usePersistentStore((state) => state.get('ig-tovd-fgzoom')) ?? 100;
  const setFgImgZoom = usePersistentStore((state) => state.set('ig-tovd-fgzoom'));
  const fgImgRef = useRef(null);

  const igname = 'tovd';
  const marginDefaults = { top: 0, left: 0, bottom: 0, right: 0 };
  const fgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setFgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));

  useZoomByMouseWheel(fgImgRef, fgImgZoom, setFgImgZoom);

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-tovd-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-tovd-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-tovd-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-tovd-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-tovd-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-tovd-background-mode'));
  const handleFgImgMouseMove = createHandleFgImgMouseMove(setFgImgPositionX, setFgImgPositionY);


  const { phaseIdsAll } = usePhaseIdTranslator(membership);
  const groupId = membership?.groupId;
  const seasonId = membership?.seasonId;

  const { data: groupDetail } = useFetchEntity('Group', groupId, 'Season>Competition');
  const { data: seasonDetail } = useFetchEntity('Season', seasonId, 'Competition');


  const metadata = useMemo(() => {
    return {
      GroupName: groupDetail?.name,
      GroupColor: groupDetail?.color,
      SeasonName: seasonDetail?.name,
      CompetitionName: seasonDetail?.Competition?.name,
      CompetitionLogo:
        seasonDetail?.Competition?.logoUrl?.replace(
          "[size]",
          "cropped_md",
        ),
      Date: dayjs().format(dateFormatIG),
    };
  }, [groupDetail, seasonDetail]);

  const { data: allGamesStatistics } = useFetchEntities(
    phaseIdsAll != null && 'Game',
    {
      filters: { phaseId: phaseIdsAll },
      extraAttributes: ["HomeTeamGoals", "AwayTeamGoals"],
      relations: 'Venue(name,logoUrl),HomeTeam,AwayTeam',
    }
  );

  const TeamStats = useMemo(() => {
    const teamStats = allGamesStatistics
      ?.filter((game) => game.HomeTeam && game.AwayTeam)
      .reduce((acc, game) => {
        if (!acc[game.HomeTeam.name]) {
          acc[game.HomeTeam.name] = {
            Name: game.HomeTeam.name,
            Logo: game.HomeTeam.logoUrl?.replace("[size]", "cropped_md"),
            GP: 0,
            GF: 0,
            GA: 0,
            Chances: 0,
          };
        }
        if (!acc[game.AwayTeam.name]) {
          acc[game.AwayTeam.name] = {
            Name: game.AwayTeam.name,
            Logo: game.AwayTeam.logoUrl?.replace("[size]", "cropped_md"),
            GP: 0,
            GF: 0,
            GA: 0,
            Chances: 0,
          };
        }
        acc[game.HomeTeam.name].GP += 1;
        acc[game.HomeTeam.name].GF += game.HomeTeamGoals;
        acc[game.HomeTeam.name].GA += game.AwayTeamGoals;
        acc[game.HomeTeam.name].Chances +=
          game.HomeTeamGoals + game.AwayTeamSaves;
        acc[game.AwayTeam.name].GP += 1;
        acc[game.AwayTeam.name].GF += game.AwayTeamGoals;
        acc[game.AwayTeam.name].GA += game.HomeTeamGoals;
        acc[game.AwayTeam.name].Chances +=
          game.AwayTeamGoals + game.HomeTeamSaves;
        return acc;
      }, {});

    return teamStats;
  }, [allGamesStatistics]);

  const data = useMemo(() => {
    const data = Object.values(TeamStats ?? {}).map((team) => ({
      ...team,
      Diff: team.GF - team.GA,
      GF_GP: (team.GF / (team.GP || 1)).toFixed(2),
      GA_GP: (team.GA / (team.GP || 1)).toFixed(2),
    }));

    const sorted = data.sort((a, b) => {
      return b.Diff - a.Diff;
    });

    return sorted.slice(0, 15);
  }, [TeamStats]);


  return (
    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Membership:</span>
        <PhaseSelector
          value={membership}
          onChange={setMembership}
          mandatory={['competitionId', 'seasonId']}
        />

        <span>Foreground image:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrl} setFgPictureUrl={setFgPictureUrl} igId={7} />
        </div>

        <span className="relative top-[-14px]">Foreground placement:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionX}
            fgImgPositionY={fgImgPositionY}
            setFgImgPositionX={setFgImgPositionX}
            setFgImgPositionY={setFgImgPositionY}
            fgImgZoom={fgImgZoom}
            setFgImgZoom={setFgImgZoom}
          />
        </div>

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={7}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={fgImgMargin}
            setMargin={setFgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <div />
      </div>

      {showData && (<DebugPrinter data={[metadata, data]}></DebugPrinter>)}

      {phaseIdsAll &&
        <article
          ref={exportRef}
          className="mx-auto w-[715px] bg-hockey-page-bg font-header text-white"
          style={{
            width: `${size?.width}px`,
            height: `${size?.height}px`,
            paddingTop: `${fgImgMargin.top}px`,
            paddingLeft: `${fgImgMargin.left}px`,
            paddingRight: `${fgImgMargin.right}px`,
            paddingBottom: `${fgImgMargin.bottom}px`,
            ...(backgroundMode === 'COLOR' && backgroundColor
              ? { backgroundColor }
              : backgroundMode === 'IMAGE' && backgroundImgUrl
                ? {
                    backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }
                : {
                    backgroundColor: '#323f50',
                  }),
          }}
        >
          <div className="flex h-full flex-col items-center justify-between">

            <div className='p-8'>
              <IGHeader
                title="Offence vs Defence power"
                league={metadata.GroupName}
                leagueStyle={{ color: metadata.GroupColor ?? '#777777' }}
                small
                date={metadata.Date}
              />
              <TableOffenceDefence data={data} />
            </div>
            <div
              ref={fgImgRef}
              onMouseMove={handleFgImgMouseMove}
              className='w-full relative my-24 aspect-[12/5]'
              style={{
                backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrl}')`,
                backgroundSize: `${fgImgZoom ?? 100}%`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: createForegroundPositionExpression(fgImgPositionX, fgImgPositionY),
                cursor: 'grab', // Visual cue for draggable behavior
                clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
              }}
              onMouseDown={(e) => e.target.style.cursor = 'grabbing'} // Change cursor while dragging
              onMouseUp={(e) => e.target.style.cursor = 'grab'} // Reset cursor
            ></div>
            <p className="text-center text-gray-400">
              Generated with: HMS | more info at: phmcup.cz/hms
            </p>
          </div>
        </article>
      }
    </>
  );
};

export default IG7_TeamOffenseVsDefense;
